import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, FileText, MessagesSquare, ShieldCheck, Clock, ArrowRight } from "lucide-react";

const Stat = ({ icon: Icon, value, label, to, navigate, testid }) => (
  <Card className="p-5 border-[#E6EDF2] cursor-pointer hover:bg-[rgba(155,185,206,0.08)] transition-colors" style={{ boxShadow: "var(--mmm-shadow-sm)" }} onClick={() => navigate(to)} data-testid={testid}>
    <Icon className="text-[#1C4358] mb-2" size={22} />
    <div className="text-3xl font-bold font-display text-[#252221] tabular-nums">{value}</div>
    <div className="text-sm text-muted-foreground flex items-center gap-1">{label} <ArrowRight size={13} /></div>
  </Card>
);

export default function PurchaserPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await api.get("/portal/purchaser/overview"); setData(res.data); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-40" /></div>;

  const approved = data?.access?.approved;
  const s = data?.stats || {};

  if (!approved) {
    return (
      <div className="space-y-6" data-testid="purchaser-portal">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221]">Welcome, {user?.full_name}</h2>
          <p className="text-sm text-muted-foreground">Investor / Purchaser portal</p>
        </div>
        <Card className="p-8 border-[#E6EDF2] text-center" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="purchaser-gate">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(183,121,31,0.14)" }}>
            <Clock size={26} className="text-[#B7791F]" />
          </div>
          <h3 className="font-display text-lg font-bold text-[#252221] mb-1">Your access is pending approval</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            Investor opportunities are shared only after an approved agreement / NDA is in place.
            Our team is reviewing your account — you'll gain access to approved property opportunities once confirmed.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ backgroundColor: "rgba(183,121,31,0.12)", color: "#9A6516" }} data-testid="purchaser-status">
            <Clock size={13} /> Status: {data?.access?.status || "pending"}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchaser-portal">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221]">Welcome, {user?.full_name}</h2>
          <p className="text-sm text-muted-foreground">Approved property opportunities curated for investors.</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ backgroundColor: "rgba(47,125,90,0.12)", color: "#256B4B" }} data-testid="purchaser-status">
          <ShieldCheck size={14} /> Approved investor
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={Building2} value={s.opportunities ?? 0} label="Opportunities" to="/portal/opportunities" navigate={navigate} testid="purchaser-stat-opportunities" />
        <Stat icon={FileText} value={s.documents ?? 0} label="Documents" to="/portal/purchaser-documents" navigate={navigate} testid="purchaser-stat-documents" />
        <Stat icon={MessagesSquare} value={s.messages ?? 0} label="Deal room messages" to="/portal/deal-room" navigate={navigate} testid="purchaser-stat-messages" />
      </div>

      <Card className="p-6 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <h3 className="font-display font-semibold text-[#252221] mb-2">How it works</h3>
        <ul className="space-y-2 text-sm text-[#252221]">
          <li className="flex items-start gap-2.5"><Building2 size={16} className="text-[#1C4358] mt-0.5" /> Browse approved property opportunities and seller timelines.</li>
          <li className="flex items-start gap-2.5"><FileText size={16} className="text-[#1C4358] mt-0.5" /> Upload your offer documents securely for our team to review.</li>
          <li className="flex items-start gap-2.5"><MessagesSquare size={16} className="text-[#1C4358] mt-0.5" /> Communicate through your controlled deal room.</li>
        </ul>
        <Button className="mt-4 text-white" style={{ backgroundColor: "var(--mmm-red)" }} onClick={() => navigate("/portal/opportunities")} data-testid="purchaser-browse-button">Browse opportunities <ArrowRight size={15} className="ml-1.5" /></Button>
      </Card>
    </div>
  );
}
